import {
  Alert,
  Button,
  Card,
  Divider,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";

import "../styles.css";

import { Context } from "../../../context";
import { Fetch, Put } from "../../../fetch";

const { Column } = Table;
const { Text } = Typography;

const MAX_SCHEDULE = 10;

const ORDINALS = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th"];

/**
 * Per-Episode Rewards with Rewatch Decay — single config doc.
 * Every finished episode pays immediately; rewatches pay the next amount in
 * the schedule (anything past the end pays 0 forever — or keeps paying the
 * last amount when "repeat last reward" is on; counts never reset).
 * A rewatch pays only after ALL episodes of the series are watched at the
 * same pass (fresh-first, anti-loop). Mutually exclusive with the Watch
 * Campaign: only one may be active at a time.
 */
const EpisodeRewardsConfig = () => {
  const { token, apiUrl } = useContext(Context);

  const [config, setConfig] = useState(null);
  const [series, setSeries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [counter, setCounter] = useState(0);
  const [addSeriesId, setAddSeriesId] = useState(null);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/episode-rewards-config/`, token, (result) => {
      if (result && result.config) setConfig(result.config);
    });
  }, [apiUrl, token, counter]);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/webseries?pageSize=100000`, token, (result) => {
      setSeries(result.webseries || []);
    });
  }, [apiUrl, token]);

  const seriesById = useMemo(() => {
    const map = {};
    series.forEach((s) => {
      map[s._id] = s;
    });
    return map;
  }, [series]);

  const set = (patch) => setConfig((c) => ({ ...c, ...patch }));
  const setNested = (key, patch) =>
    setConfig((c) => ({ ...c, [key]: { ...(c[key] || {}), ...patch } }));

  const save = (extra) => {
    const c = { ...config, ...(extra || {}) };
    const body = {
      status: c.status,
      reward_schedule: (c.reward_schedule || []).map((n) => Number(n) || 0),
      repeat_last_reward: !!c.repeat_last_reward,
      min_watch_pct: c.min_watch_pct || 80,
      daily_coin_cap: c.daily_coin_cap || 0,
      subscriber_enabled: !!c.subscriber_enabled,
      audience: c.audience || "ag_registered",
      eligibility: c.eligibility,
      velocity: c.velocity,
      info_points: c.info_points || [],
      info_points_hi: c.info_points_hi || [],
    };
    setSaving(true);
    Put(
      `${apiUrl}/v4/episode-rewards-config/`,
      token,
      body,
      (result) => {
        setSaving(false);
        if (result && result.config) {
          setConfig(result.config);
          notification.success({ message: "Episode Rewards saved" });
        } else {
          notification.error({
            message: (result && result.detail) || "Save failed",
          });
          setCounter((n) => n + 1);
        }
      },
      () => {
        setSaving(false);
        notification.error({ message: "Save failed" });
        setCounter((n) => n + 1);
      }
    );
  };

  if (!config) return null;

  const schedule = config.reward_schedule || [];
  const eligibility = config.eligibility || {};
  const velocity = config.velocity || {};
  const selectedIds = eligibility.series_ids || [];
  const active = config.status === "active";

  const updateSchedule = (index, value) => {
    const next = schedule.map((c, i) => (i === index ? value || 0 : c));
    set({ reward_schedule: next });
  };

  return (
    <div className="manage-tasks">
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Alert
          type={active ? "success" : "warning"}
          showIcon
          message={
            active
              ? `Episode Rewards LIVE (version ${config.version || 0}) — every finished episode pays instantly.`
              : "Episode Rewards paused — users see nothing until activated."
          }
          description="Rewatch amounts decay per the schedule below and NEVER reset. A rewatch pays only after the whole series is watched (fresh episodes first). Can run alongside the Watch Campaign — one watch may then earn in both (each has its own daily cap)."
          action={
            <Switch
              checked={active}
              checkedChildren="Active"
              unCheckedChildren="Paused"
              loading={saving}
              onChange={(checked) =>
                save({ status: checked ? "active" : "paused" })
              }
            />
          }
        />

        <Card
          title="Reward schedule (coins per watch of the same episode)"
          size="small"
        >
          <Table
            dataSource={schedule.map((coins, i) => ({ key: i, coins, index: i }))}
            pagination={false}
            size="small"
          >
            <Column
              title="Watch"
              width={110}
              render={(_, r) => ORDINALS[r.index] || `${r.index + 1}th`}
            />
            <Column
              title="Coins"
              render={(_, r) => (
                <InputNumber
                  min={0}
                  value={r.coins}
                  onChange={(v) => updateSchedule(r.index, v)}
                />
              )}
            />
            <Column
              width={60}
              render={(_, r) => (
                <Popconfirm
                  title="Remove this watch step?"
                  onConfirm={() =>
                    set({
                      reward_schedule: schedule.filter((_, i) => i !== r.index),
                    })
                  }
                >
                  <Button danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            />
          </Table>
          <Space style={{ marginTop: 12 }}>
            <Button
              icon={<PlusOutlined />}
              disabled={schedule.length >= MAX_SCHEDULE}
              onClick={() =>
                set({
                  reward_schedule: [
                    ...schedule,
                    Math.max(1, Math.floor((schedule[schedule.length - 1] || 10) / 2)),
                  ],
                })
              }
            >
              Add watch step
            </Button>
            <Space size={4}>
              <Switch
                size="small"
                checked={!!config.repeat_last_reward}
                onChange={(v) => set({ repeat_last_reward: v })}
              />
              <Text>Keep paying the last amount after the schedule ends</Text>
            </Space>
            <Tag color="gold">
              {config.repeat_last_reward && schedule.length
                ? `After the ${ORDINALS[schedule.length - 1] || "last"} watch: ${
                    schedule[schedule.length - 1] || 0
                  } coins per watch, forever`
                : `After the ${ORDINALS[schedule.length - 1] || "last"} watch: 0 coins forever`}
            </Tag>
          </Space>
        </Card>

        <Card title="Eligibility" size="small">
          <Space size="large" wrap>
            <Space direction="vertical" size={2}>
              <Text>Content scope</Text>
              <Select
                style={{ width: 220 }}
                value={eligibility.scope || "all"}
                onChange={(v) => setNested("eligibility", { scope: v })}
              >
                <Select.Option value="all">All series</Select.Option>
                <Select.Option value="selected_series">
                  Selected series only
                </Select.Option>
              </Select>
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Exclude shareable content</Text>
              <Switch
                checked={eligibility.exclude_shareable !== false}
                onChange={(v) => setNested("eligibility", { exclude_shareable: v })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Audience</Text>
              <Select
                style={{ width: 200 }}
                value={config.audience || "ag_registered"}
                onChange={(v) => set({ audience: v })}
              >
                <Select.Option value="ag_registered">
                  AG-registered users only
                </Select.Option>
                <Select.Option value="all">All MiniPIX users</Select.Option>
              </Select>
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Subscribers can earn</Text>
              <Switch
                checked={!!config.subscriber_enabled}
                onChange={(v) => set({ subscriber_enabled: v })}
              />
            </Space>
          </Space>
          {(eligibility.scope || "all") === "selected_series" && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <Table
                dataSource={selectedIds.map((id) => ({ key: id, id }))}
                pagination={false}
                size="small"
              >
                <Column
                  title="Series"
                  render={(_, r) =>
                    (seriesById[r.id] && seriesById[r.id].title) || (
                      <Text type="danger">{r.id} (not found)</Text>
                    )
                  }
                />
                <Column
                  title="Episodes"
                  width={100}
                  render={(_, r) =>
                    (seriesById[r.id] && seriesById[r.id].numberOfEpisodes) || "-"
                  }
                />
                <Column
                  width={60}
                  render={(_, r) => (
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        setNested("eligibility", {
                          series_ids: selectedIds.filter((id) => id !== r.id),
                        })
                      }
                    />
                  )}
                />
              </Table>
              <Space style={{ marginTop: 12 }}>
                <Select
                  showSearch
                  allowClear
                  placeholder="Add series"
                  style={{ width: 320 }}
                  value={addSeriesId}
                  onChange={setAddSeriesId}
                  optionFilterProp="children"
                >
                  {series
                    .filter((s) => !selectedIds.includes(s._id))
                    .map((s) => (
                      <Select.Option key={s._id} value={s._id}>
                        {s.title}
                      </Select.Option>
                    ))}
                </Select>
                <Button
                  icon={<PlusOutlined />}
                  disabled={!addSeriesId}
                  onClick={() => {
                    setNested("eligibility", {
                      series_ids: [...selectedIds, addSeriesId],
                    });
                    setAddSeriesId(null);
                  }}
                >
                  Add
                </Button>
              </Space>
            </>
          )}
        </Card>

        <Card title="Limits & anti-farming" size="small">
          <Space size="large" wrap>
            <Space direction="vertical" size={2}>
              <Text>Min watch %</Text>
              <InputNumber
                min={1}
                max={100}
                value={config.min_watch_pct}
                onChange={(v) => set({ min_watch_pct: v || 80 })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Daily coin cap (0 = uncapped here)</Text>
              <InputNumber
                min={0}
                value={config.daily_coin_cap}
                onChange={(v) => set({ daily_coin_cap: v || 0 })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Rewatch cooldown (seconds)</Text>
              <InputNumber
                min={0}
                value={velocity.min_rewatch_gap_seconds}
                onChange={(v) =>
                  setNested("velocity", { min_rewatch_gap_seconds: v || 0 })
                }
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Max paid watches / hour (0 = off)</Text>
              <InputNumber
                min={0}
                value={velocity.max_paid_watches_per_hour}
                onChange={(v) =>
                  setNested("velocity", { max_paid_watches_per_hour: v || 0 })
                }
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Max paid watches / day (0 = off)</Text>
              <InputNumber
                min={0}
                value={velocity.max_paid_watches_per_day}
                onChange={(v) =>
                  setNested("velocity", { max_paid_watches_per_day: v || 0 })
                }
              />
            </Space>
          </Space>
        </Card>

        <Card title="Info points (app 'how it works')" size="small">
          <Text type="secondary">One point per line.</Text>
          <Divider style={{ margin: "8px 0" }} />
          <Space direction="vertical" style={{ width: "100%" }}>
            <Input.TextArea
              rows={3}
              placeholder="English"
              value={(config.info_points || []).join("\n")}
              onChange={(e) => set({ info_points: e.target.value.split("\n") })}
            />
            <Input.TextArea
              rows={3}
              placeholder="Hindi"
              value={(config.info_points_hi || []).join("\n")}
              onChange={(e) =>
                set({ info_points_hi: e.target.value.split("\n") })
              }
            />
          </Space>
        </Card>

        <Button type="primary" loading={saving} onClick={() => save()}>
          Save Episode Rewards
        </Button>
      </Space>
    </div>
  );
};

export default EpisodeRewardsConfig;
